import { useEffect, useState } from "react";
import { Plus, Vote, Pencil, Trash2, Lock, Unlock, Check, X, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { useStages } from "../lib/hooks";
import { Empty, Field, Loader, PageHeader } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const PRESETS = ["Melhor lado esquerdo", "Melhor lado direito", "Atleta destaque", "Melhor dupla"];
const toCats = (cats) => cats.map((c) => ({ ...c, text: c.options.map((o) => o.label).join("\n") }));

const PollForm = ({ open, onOpenChange, poll, onSaved }) => {
  const stages = useStages();
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF(poll ? { ...poll, stage_id: poll.stage_id || "", categories: toCats(poll.categories) }
      : { title: "", description: "", stage_id: "", status: "open", show_results: true, categories: [{ label: "Melhor lado esquerdo", text: "" }, { label: "Melhor lado direito", text: "" }, { label: "Atleta destaque", text: "" }] });
  }, [open, poll]);
  const setCat = (i, k, v) => setF({ ...f, categories: f.categories.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });
  const importStage = async (kind) => {
    if (!f.stage_id) return toast.error("Selecione uma etapa primeiro");
    const { data } = await api.get(`/stages/${f.stage_id}/registrations`);
    const names = kind === "duplas" ? data.map((r) => r.label) : [...new Set(data.flatMap((r) => [r.player1?.name, r.player2?.name]).filter(Boolean))].sort();
    setF({ ...f, categories: f.categories.map((c) => ({ ...c, text: names.join("\n") })) });
    toast.success(`${names.length} opções importadas`);
  };
  const save = async (e) => {
    e.preventDefault();
    const categories = f.categories.map((c) => {
      const old = c.options || [];
      const options = c.text.split("\n").map((s) => s.trim()).filter(Boolean).map((label) => ({ id: old.find((o) => o.label === label)?.id, label }));
      return { id: c.id, label: c.label, options };
    });
    const body = { title: f.title, description: f.description, stage_id: f.stage_id || null, status: f.status, show_results: f.show_results, categories };
    try {
      if (poll) await api.put(`/polls/${poll.id}`, body); else await api.post("/polls", body);
      toast.success("Votação salva"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };
  if (!f.categories) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#232B3E] bg-[#121722] sm:max-w-xl">
        <DialogHeader><DialogTitle className="h-display text-3xl">{poll ? "Editar votação" : "Nova votação"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Título"><input required data-testid="poll-title-input" className="inp" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Destaques da 1ª Etapa" /></Field>
          <Field label="Descrição"><input data-testid="poll-description-input" className="inp" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Etapa">
              <select data-testid="poll-stage-select" className="inp" value={f.stage_id} onChange={(e) => setF({ ...f, stage_id: e.target.value })}><option value="">Nenhuma</option>{stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            </Field>
            <Field label="Resultados">
              <select data-testid="poll-results-select" className="inp" value={f.show_results ? "1" : "0"} onChange={(e) => setF({ ...f, show_results: e.target.value === "1" })}><option value="1">Visíveis a todos</option><option value="0">Só ao encerrar</option></select>
            </Field>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid="poll-import-athletes" onClick={() => importStage("atletas")} className="btn-ghost flex items-center gap-1 text-xs"><Download className="h-3.5 w-3.5" />Importar atletas da etapa</button>
            <button type="button" data-testid="poll-import-duplas" onClick={() => importStage("duplas")} className="btn-ghost flex items-center gap-1 text-xs"><Download className="h-3.5 w-3.5" />Importar duplas da etapa</button>
          </div>
          {f.categories.map((c, i) => (
            <div key={i} className="rounded-xl border border-[#232B3E] p-3 space-y-2">
              <div className="flex gap-2">
                <input data-testid={`poll-cat-label-${i}`} className="inp" list="poll-presets" value={c.label} onChange={(e) => setCat(i, "label", e.target.value)} placeholder="Categoria" />
                <button type="button" onClick={() => setF({ ...f, categories: f.categories.filter((_, j) => j !== i) })} className="px-2 text-[#64748B] hover:text-red-400"><X className="h-4 w-4" /></button>
              </div>
              <textarea data-testid={`poll-cat-options-${i}`} rows={4} className="inp" value={c.text} onChange={(e) => setCat(i, "text", e.target.value)} placeholder="Uma opção por linha (nome do atleta ou dupla)" />
            </div>
          ))}
          <datalist id="poll-presets">{PRESETS.map((p) => <option key={p} value={p} />)}</datalist>
          <button type="button" data-testid="poll-add-category" onClick={() => setF({ ...f, categories: [...f.categories, { label: "", text: "" }] })} className="btn-ghost w-full">+ Categoria</button>
          <button data-testid="poll-save-btn" className="btn-lime w-full rounded-full py-3">Salvar votação</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PollCard = ({ p, isAdmin, onEdit, onReload }) => {
  const vote = async (cat, opt) => {
    try { await api.post(`/polls/${p.id}/vote`, { category_id: cat, option_id: opt }); toast.success("Voto registrado!"); onReload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async () => { await api.put(`/polls/${p.id}`, { ...p, stage_id: p.stage_id || null, status: p.status === "open" ? "closed" : "open" }); onReload(); };
  const del = async () => { if (!window.confirm("Excluir votação?")) return; await api.delete(`/polls/${p.id}`); onReload(); };
  const open = p.status === "open";
  return (
    <div data-testid={`poll-${p.id}`} className="card-x p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2"><span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase ${open ? "bg-[#E4F222] text-black" : "bg-[#1A202E] text-[#94A3B8]"}`}>{open ? "Aberta" : "Encerrada"}</span><span className="text-xs text-[#64748B]">{p.total_voters} votante(s)</span></div>
          <div className="h-display mt-1 text-3xl">{p.title}</div>
          {p.description && <p className="text-sm text-[#94A3B8]">{p.description}</p>}
        </div>
        {isAdmin && (
          <div className="flex gap-1">
            <button data-testid={`poll-toggle-${p.id}`} onClick={toggle} className="btn-ghost flex items-center gap-1 text-xs">{open ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}{open ? "Encerrar" : "Reabrir"}</button>
            <button data-testid={`poll-edit-${p.id}`} onClick={() => onEdit(p)} className="rounded-lg p-2 text-[#94A3B8] hover:bg-[#1A202E]"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`poll-delete-${p.id}`} onClick={del} className="rounded-lg p-2 text-[#94A3B8] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        {p.categories.map((c) => {
          const counts = p.results?.[c.id] || {};
          const total = Object.values(counts).reduce((a, b) => a + b, 0);
          return (
            <div key={c.id}>
              <div className="mb-2 text-sm font-bold uppercase tracking-wider text-[#E4F222]">{c.label}</div>
              <div className="space-y-1.5">
                {c.options.map((o) => {
                  const mine = p.my_votes[c.id] === o.id;
                  const pct = total ? Math.round(((counts[o.id] || 0) / total) * 100) : 0;
                  return (
                    <button key={o.id} disabled={!open} data-testid={`vote-${c.id}-${o.id}`} onClick={() => vote(c.id, o.id)}
                      className={`relative w-full overflow-hidden rounded-xl border px-3 py-2.5 text-left text-sm ${mine ? "border-[#E4F222]" : "border-[#232B3E] hover:border-[#E4F222]/50"} disabled:cursor-default`}>
                      {p.results && <div className="absolute inset-y-0 left-0 bg-[#E4F222]/10" style={{ width: `${pct}%` }} />}
                      <span className="relative flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2">{mine && <Check className="h-4 w-4 text-[#E4F222]" />}{o.label}</span>
                        {p.results && <span className="text-xs text-[#94A3B8]">{counts[o.id] || 0} · {pct}%</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function Polls() {
  const { isAdmin } = useAuth();
  const [list, setList] = useState(null);
  const [dlg, setDlg] = useState({ open: false, poll: null });
  const load = () => api.get("/polls").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div>
      <PageHeader eyebrow="Comunidade" title="Votação" subtitle="Vote nos destaques de cada etapa."
        action={isAdmin && <button data-testid="new-poll-btn" onClick={() => setDlg({ open: true, poll: null })} className="btn-lime flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Plus className="h-4 w-4" />Nova votação</button>} />
      {!list ? <Loader /> : list.length === 0 ? <Empty icon={Vote} title="Nenhuma votação" text="Em breve você poderá votar nos destaques do circuito." /> : (
        <div className="space-y-4">{list.map((p) => <PollCard key={p.id} p={p} isAdmin={isAdmin} onEdit={(x) => setDlg({ open: true, poll: x })} onReload={load} />)}</div>
      )}
      {isAdmin && <PollForm open={dlg.open} poll={dlg.poll} onOpenChange={(o) => setDlg({ ...dlg, open: o })} onSaved={load} />}
    </div>
  );
}
